import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'
import { useLocation } from 'react-router-dom'

import { addToast } from 'components/Toast/Toast'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { putProfileSettings } from 'services/endpoints/settings/account/profileSettings'
import { putChatSettings } from 'services/endpoints/settings/general/chat'
import { useUserProfile } from 'store'

interface TSingleSetting {
    section: string
    title: string
    withoutHeaderSwitch?: boolean
    withHeaderUnderline?: boolean
    body?: JSX.Element
}

export const SingleSetting = ({
    section,
    title,
    withoutHeaderSwitch,
    withHeaderUnderline = true,
    body
}: TSingleSetting) => {
    const [settingActive, setSettingActive] = useState<boolean>(false)

    const userData = useUserProfile()!
    const queryClient = useQueryClient()
    const location = useLocation()
    const { t } = useTranslation()

    const chatSettingsMutation = useMutation(
        async (params: any) => await putChatSettings(params),
        {
            onMutate: async (newData: Record<string, boolean | string>) => {
                await queryClient.cancelQueries('user-profile')
                const previousQueryData =
                    queryClient.getQueryData('user-profile')
                queryClient.setQueryData('user-profile', (oldData: any) => {
                    return {
                        ...oldData,
                        chat_settings: {
                            ...oldData.chat_settings,
                            ...newData
                        }
                    }
                })
                return { previousQueryData }
            },
            onError: (error, newData, context: any) => {
                console.error(error)
                queryClient.setQueryData(
                    'user-profile',
                    context.previousQueryData
                )
                addToast('error', t('error:errorSomethingWentWrong'))
            }
        }
    )

    useEffect(() => {
        const checkActiveStatus = () => {
            switch (location.pathname) {
                case '/settings/general/profile-settings/likes-count':
                    return userData.like_count
                case '/settings/general/profile-settings/follower-count':
                    return userData.follow_count
                case '/settings/general/profile-settings/online-status':
                    return userData.show_online_status
                case '/settings/general/profile-settings/friend-list':
                    return userData.public_friends
                case '/settings/general/profile-settings/camshow-requests':
                    return userData.allow_camshow_request

                case '/settings/general/chat-settings/connect-instagram':
                    return userData.chat_settings.connect_instagram_enabled
                case '/settings/general/chat-settings/welcome-message':
                    return userData?.chat_settings?.welcome_message_enabled

                case '/settings/general/profile-settings/mini-bio':
                    return userData.show_mini_bio

                default:
                    return false
            }
        }

        setSettingActive(checkActiveStatus())
    }, [userData, location.pathname])

    const updateProfileSettings = (key: string, value: boolean) => {
        setSettingActive(value)
        putProfileSettings({ [key]: value }).then(() => {
            queryClient.invalidateQueries('user-profile')
        })
    }
    const updateChatSettings = (key: string, value: boolean) => {
        setSettingActive(value)
        putChatSettings({ [key]: value }).then(() => {
            queryClient.invalidateQueries('user-profile')
        })
    }

    const toggleFunction = () => {
        setSettingActive(!settingActive)
        switch (location.pathname) {
            case '/settings/general/profile-settings/likes-count':
                updateProfileSettings(
                    'like_count',
                    !userData.like_count
                )
                break
            case '/settings/general/profile-settings/follower-count':
                updateProfileSettings(
                    'follow_count',
                    !userData.follow_count
                )
                break
            case '/settings/general/profile-settings/online-status':
                updateProfileSettings(
                    'show_online_status',
                    !userData.show_online_status
                )
                break
            case '/settings/general/profile-settings/friend-list':
                updateProfileSettings(
                    'public_friends',
                    !userData.public_friends
                )
                break
            case '/settings/general/profile-settings/mini-bio':
                updateProfileSettings('show_mini_bio', !userData.show_mini_bio)
                break
            case '/settings/general/profile-settings/camshow-requests':
                updateProfileSettings(
                    'allow_camshow_request',
                    !userData.allow_camshow_request
                )
                break

            case '/settings/general/chat-settings/welcome-message':
                chatSettingsMutation.mutate({
                    welcome_message_enabled:
                        !userData?.chat_settings?.welcome_message_enabled
                })
                break
            case '/settings/general/chat-settings/connect-instagram':
                updateChatSettings(
                    'connect_instagram_enabled',
                    !userData.chat_settings.connect_instagram_enabled
                )
                break

            default:
        }
    }

    return (
        <WithHeaderSection
            customClass='single-setting-withheader-section'
            withoutBorder={!withHeaderUnderline}
            headerSection={
                withoutHeaderSwitch ? (
                    <LayoutHeader
                        type='basic'
                        section={section}
                        title={title.split('-').join(' ')}
                    />
                ) : (
                    <LayoutHeader
                        type='switch'
                        section={section}
                        title={title.split('-').join(' ')}
                        switchActive={settingActive}
                        switchFn={() => {
                            toggleFunction()
                        }}
                    />
                )
            }
        >
            <>{body ?? ''}</>
        </WithHeaderSection>
    )
}
